// Unified personal inbox — pinned to the top nav for every authenticated
// user. Merges event message threads (organizer↔vendor) with Book-Now
// inquiries into a single newest-first list. Actions per row:
//   * Open — deep-links to the source page (existing thread UI).
//   * Mark read — flips read flags in the underlying source.
//   * Delete — hides for the current user only (counterparty still sees it).
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Inbox as InboxIcon, MailOpen, Trash2, CheckCheck, CalendarClock,
  Handshake, ArrowRight, Loader2, Search, X as XIcon, ShieldCheck,
} from "lucide-react";

const TYPE_META = {
  event_message:  { label: "Event thread",    icon: CalendarClock, ink: "var(--avt-purple)" },
  vendor_inquiry: { label: "Booking inquiry", icon: Handshake,      ink: "var(--terracotta)" },
  admin_message:  { label: "AddVendTour team", icon: ShieldCheck,    ink: "var(--avt-purple)" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Inbox() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/inbox");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // Client-side search filter — declared before any early return so the
  // hook order is stable across renders.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => {
      const hay = [t.counterparty, t.title, t.snippet]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/inbox" replace />;

  const markRead = async (thread_id) => {
    setBusyId(thread_id);
    try {
      await api.post("/inbox/read", { thread_id });
      toast.success("Marked as read");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusyId(""); }
  };

  const remove = async (thread_id) => {
    if (!window.confirm("Hide this conversation from your inbox? The other side will still see it.")) return;
    setBusyId(thread_id);
    try {
      await api.post("/inbox/delete", { thread_id });
      toast.success("Removed from your inbox");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusyId(""); }
  };

  const markAll = async () => {
    const unread = items.filter((i) => i.unread > 0);
    if (!unread.length) return;
    if (!window.confirm(`Mark all ${unread.length} unread conversation${unread.length === 1 ? "" : "s"} as read?`)) return;
    setBusyId("__all__");
    try {
      await Promise.all(unread.map((i) => api.post("/inbox/read", { thread_id: i.id })));
      toast.success("All caught up");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusyId(""); }
  };

  const unreadTotal = items.reduce((n, i) => n + (i.unread || 0), 0);

  return (
    <div className="mx-auto max-w-4xl px-6 py-14" data-testid="inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Inbox</div>
          <h1 className="font-serif text-5xl mt-3">Your mailbox.</h1>
          <p className="mt-3 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Every conversation across AddVendTour &mdash; event threads,
            booking inquiries, and more &mdash; newest first. Mark them
            read, or clear ones you&rsquo;re done with.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/inbox/admin"
            data-testid="inbox-contact-support"
            className="rounded-full inline-flex items-center gap-2 px-4 py-2 text-sm border-2 hover:bg-black/[0.03] transition-colors"
            style={{
              borderColor: "var(--avt-purple)",
              color: "var(--avt-purple)",
            }}
          >
            <ShieldCheck size={14} />
            Contact AddVendTour
          </Link>
          {unreadTotal > 0 && (
            <Button
              variant="outline"
              onClick={markAll}
              disabled={busyId === "__all__"}
              data-testid="inbox-mark-all-read"
              className="rounded-full inline-flex items-center gap-2"
            >
              {busyId === "__all__" ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
              Mark all as read
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-6 rounded-md p-3 text-sm"
             style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}>
          {error}
        </div>
      )}

      <div className="mt-8">
        {loading ? (
          <div className="py-16 text-center text-sm" style={{ color: "var(--ink-soft)" }}>
            Loading your mailbox…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="inbox-empty-state"
            className="py-16 text-center rounded-lg border"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <InboxIcon size={40} className="mx-auto mb-3" style={{ color: "var(--ink-soft)" }} />
            <div className="font-serif text-2xl">Inbox zero.</div>
            <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              Nothing here yet. Every new message will land right on this page.
            </p>
          </div>
        ) : (
          <>
            {/* Search bar — client-side filter across counterparty + title +
                snippet. Kept above the list so scrolling the list doesn't
                push it off-screen on short viewports. */}
            <div className="mb-4 flex items-center gap-2">
              <div className="relative flex-1">
                <Search
                  size={14}
                  className="absolute top-1/2 -translate-y-1/2 left-3 opacity-50 pointer-events-none"
                />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, event, or keyword…"
                  className="pl-9 pr-9"
                  data-testid="inbox-search-input"
                  autoComplete="off"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    data-testid="inbox-search-clear"
                    aria-label="Clear search"
                    className="absolute top-1/2 -translate-y-1/2 right-3 opacity-60 hover:opacity-100"
                  >
                    <XIcon size={14} />
                  </button>
                )}
              </div>
              {query && (
                <div
                  className="text-xs shrink-0"
                  style={{ color: "var(--ink-soft)" }}
                  data-testid="inbox-search-count"
                >
                  {filtered.length} of {items.length}
                </div>
              )}
            </div>

            {filtered.length === 0 ? (
              <div
                data-testid="inbox-no-search-results"
                className="py-12 text-center rounded-lg border"
                style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
              >
                <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
                  No conversations match &ldquo;{query}&rdquo;.
                </p>
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="mt-2 text-xs underline"
                  style={{ color: "var(--terracotta)" }}
                  data-testid="inbox-no-results-clear"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <ul
                data-testid="inbox-list"
                className="divide-y rounded-lg border overflow-hidden"
                style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
              >
                {filtered.map((t) => {
              const meta = TYPE_META[t.type] || TYPE_META.event_message;
              const Icon = meta.icon;
              const isUnread = t.unread > 0;
              return (
                <li
                  key={t.id}
                  data-testid={`inbox-row-${t.id}`}
                  className="px-5 py-4 flex flex-wrap sm:flex-nowrap items-start gap-4 transition"
                  style={{ background: isUnread ? "rgba(196,79,54,0.04)" : "#fff" }}
                >
                  <div className="shrink-0 mt-1">
                    <div
                      className="w-9 h-9 rounded-full inline-flex items-center justify-center"
                      style={{ background: "rgba(0,0,0,0.05)", color: meta.ink }}
                    >
                      <Icon size={16} />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => nav(t.link)}
                    data-testid={`inbox-open-${t.id}`}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                            style={{ background: "rgba(0,0,0,0.05)", color: meta.ink }}>
                        {meta.label}
                      </span>
                      <span className={`font-medium truncate ${isUnread ? "" : ""}`}>
                        {t.title}
                      </span>
                      {isUnread && (
                        <span
                          className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full"
                          style={{ background: "var(--terracotta)", color: "#fff" }}
                          data-testid={`inbox-unread-${t.id}`}
                        >
                          {t.unread} new
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm truncate" style={{ color: "var(--ink)" }}>
                      <span className="font-medium">{t.counterparty}</span>
                      <span className="mx-1.5" style={{ color: "var(--ink-soft)" }}>·</span>
                      <span style={{ color: "var(--ink-soft)" }}>{t.snippet || "(no message body)"}</span>
                    </div>
                    <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                      {timeAgo(t.last_message_at)}
                      {t.message_count > 1 && ` · ${t.message_count} messages`}
                    </div>
                  </button>

                  <div className="flex items-center gap-1 shrink-0">
                    {isUnread && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markRead(t.id)}
                        disabled={busyId === t.id}
                        data-testid={`inbox-read-${t.id}`}
                        className="rounded-full inline-flex items-center gap-1"
                      >
                        <MailOpen size={13} />
                        <span className="hidden sm:inline text-xs">Mark read</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(t.id)}
                      disabled={busyId === t.id}
                      data-testid={`inbox-delete-${t.id}`}
                      className="rounded-full inline-flex items-center gap-1"
                      style={{ color: "var(--terracotta)" }}
                    >
                      <Trash2 size={13} />
                      <span className="hidden sm:inline text-xs">Delete</span>
                    </Button>
                    <Link
                      to={t.link}
                      data-testid={`inbox-arrow-${t.id}`}
                      className="rounded-full inline-flex items-center gap-1 p-2 hover:bg-black/5"
                      aria-label="Open thread"
                    >
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </li>
              );
            })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
